import { useStore } from '@nanostores/react';
import { forwardRef, memo, useCallback, useRef, type RefObject } from 'react';
import { ClientOnly } from 'remix-utils/client-only';
import { FileAttachments } from './FileAttachments.client';
import { FileSelections } from './FileSelections.client';
import { PromptFocusedFiles } from './PromptFocusedFiles.client';
import { PromptInspectedElement } from './PromptInspectedElement.client';
import { PromptSubscribeBanner } from './PromptSubscribeBanner.client';
import { SendButton } from './SendButton.client';
import { FeaturePreviews } from '~/components/chat/FeaturePreviews.client';
import { PromptOverlay } from '~/components/chat/PromptOverlay.client';
import { PromptLibrary } from '~/components/chat/commands/PromptLibrary';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { PromptDropZone } from '~/components/ui/PromptDropZone.client';
import { useDebounce } from '~/lib/hooks/useThrottle';
import type { PromptMode } from '~/lib/prompts/types';
import { userStore } from '~/lib/stores/user';
import type { FileAttachment, FileSelection } from '~/types/chat';
import { isValidAttachment } from '~/utils/attachments.client';
import { waitForChameleon, showChameleonHelpbar } from '~/utils/chameleon';
import { classNames } from '~/utils/classNames';
import { SUPPORTED_PROMPT_FILE_TYPES_LIST, SUPPORTED_PROMPT_FILE_TYPES_SET } from '~/utils/constants';

import { isInspectedImage } from '~/utils/inspect-element';
import styles from './Prompt.module.scss';

const TEXTAREA_MIN_HEIGHT = 76;

export interface PromptProps {
  isReadOnly?: boolean;
  textareaRef?: RefObject<HTMLTextAreaElement>;
  chatStarted?: boolean;
  isDragging?: boolean;
  isStreaming?: boolean;
  disableInput?: boolean;
  enhancingPrompt?: boolean;
  promptEnhanced?: boolean;
  input?: string;
  fileSelections?: FileSelection[];
  attachments?: FileAttachment[];
  mode?: PromptMode;
  onModeChange?: (mode: PromptMode) => void;
  handleDrop?: (event: React.DragEvent<HTMLDivElement>) => Promise<void>;
  onUploadFile?: (file: File) => Promise<void> | void;
  onRemoveFileSelection?: (index: number) => void;
  onRemoveAttachment?: (attachment: FileAttachment) => void;
  handleStop?: () => void;
  sendMessage?: (messageInput?: string) => void;
  handleInputChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  enhancePrompt?: () => void;
}

export const Prompt = memo(
  forwardRef<HTMLDivElement, PromptProps>((props, ref) => {
    const {
      textareaRef,
      isReadOnly,
      chatStarted = false,
      isDragging = false,
      isStreaming = false,
      enhancingPrompt = false,
      promptEnhanced = false,
      disableInput = false,
      fileSelections = [],
      input = '',
      mode = 'build',
      onModeChange,
      handleDrop,
      onUploadFile,
      onRemoveFileSelection,
      onRemoveAttachment,
      sendMessage,
      enhancePrompt,
      handleStop,
      handleInputChange,
    } = props;

    const userData = useStore(userStore.user);

    let placeholder = mode === 'build' ? 'How can Bolt help you today? (or /command)' : 'What do you want to discuss?';

    if (!chatStarted && mode === 'build') {
      placeholder = "Type your idea and we'll build it together.";
    }

    const attachments = props.attachments?.filter((attachment) => !isInspectedImage(attachment.file.name)) ?? [];

    const TEXTAREA_MAX_HEIGHT = chatStarted ? 400 : 200;

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handlePaste = useCallback(
      async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const { items } = event.clipboardData;

        for (let i = 0; i < items.length; i++) {
          if (items[i].type.includes('image')) {
            const file = items[i].getAsFile();

            if (!isValidAttachment(file, { allowedExtensions: SUPPORTED_PROMPT_FILE_TYPES_SET })) {
              break;
            }

            if (file) {
              event.preventDefault();

              onUploadFile?.(file);

              break;
            }
          }
        }
      },
      [onUploadFile],
    );

    const handleSelectFile = useCallback(() => {
      fileInputRef.current?.click();
    }, [fileInputRef]);

    const handleFilesSelected = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        if (files) {
          for (const file of Array.from(files)) {
            onUploadFile?.(file);
          }
        }

        event.target.value = '';
      },
      [onUploadFile],
    );

    const hasAttachments = fileSelections.length > 0 || attachments.length > 0;

    const setInput = useCallback(
      (input: string) => {
        const textarea = textareaRef?.current;

        if (!textarea) {
          return;
        }

        if (input === '') {
          textarea.value = input;

          handleInputChange?.({ target: textarea } as React.ChangeEvent<HTMLTextAreaElement>);

          return;
        }

        textarea.focus();
        textarea.select();

        document.execCommand('insertText', false, input);

        handleInputChange?.({ target: textarea } as React.ChangeEvent<HTMLTextAreaElement>);

        textarea.selectionStart = input.length;
        textarea.selectionEnd = input.length;
      },
      [handleInputChange],
    );

    const focusPromptInput = useCallback(() => {
      requestAnimationFrame(() => {
        textareaRef?.current?.focus();
      });
    }, [textareaRef]);

    const handleSendMessage = () => {
      sendMessage?.();
      focusPromptInput(); // keep focus on input after sending message
    };

    const handleShowHelpbar = useCallback(() => {
      waitForChameleon().then(() => {
        showChameleonHelpbar();
      });
    }, []);

    const debouncedShowHelpbar = useDebounce(handleShowHelpbar, 300);

    return (
      <div
        ref={ref}
        className={classNames('relative px-[var(--chat-padding)] w-full max-w-chat mx-auto z-prompt', {
          'sticky bottom-0': chatStarted,
        })}
      >
        {chatStarted && <PromptLibrary input={input} setInput={setInput} focusPromptInput={focusPromptInput} />}

        <ClientOnly>{() => <PromptSubscribeBanner />}</ClientOnly>

        <div
          className={classNames('relative shadow-xs p-[1px] overflow-clip', {
            'rounded-[0.375rem]': chatStarted,
            'rounded-2xl': !chatStarted,
          })}
        >
          {chatStarted && (
            <>
              <div className="absolute inset-0 bg-bolt-elements-prompt-border -z-1" />
              <div
                className={classNames(
                  styles.PromptContainer,
                  'absolute top-0 left-0 right-0 h-[136px] rounded-[calc(0.375rem-1px)] -z-1',
                )}
              />
              <div className="absolute top-0 left-[10px] h-px w-[100px] bg-gradient-to-r from-accent-300/0 via-accent-300 to-accent-300/0 mix-blend-overlay" />
            </>
          )}
          <div
            className={classNames({
              'bg-bolt-elements-prompt-background rounded-[calc(0.375rem-1px)]': chatStarted,
              'relative rounded-2xl dark:bg-[rgba(31,31,31,0.95)] bg-white backdrop-blur-[7px]': !chatStarted,
              [styles.GradientBorder]: !chatStarted,
            })}
          >
            <ClientOnly>
              {() => {
                return (
                  <>
                    <PromptDropZone show={isDragging} onDrop={handleDrop} disabled={isReadOnly} />

                    <PromptOverlay />

                    {(fileSelections.length > 0 || attachments.length > 0) && (
                      <div className="flex flex-col gap-5 bg-bolt-elements-background-depth-1 py-5 rounded-t-lg">
                        {fileSelections.length > 0 && (
                          <div className="max-h-[50vh] overflow-y-auto">
                            <FileSelections
                              className="px-5"
                              itemClassName="bg-bolt-elements-prompt-selections-background"
                              buttonClassName="text-bolt-elements-prompt-selections-text hover:text-bolt-elements-prompt-selections-textHover"
                              fileSelections={fileSelections}
                              allowDelete
                              onFileSelectionDelete={onRemoveFileSelection}
                            />
                          </div>
                        )}
                        {attachments.length > 0 && (
                          <FileAttachments
                            className="flex pt-[9px] gap-5 px-5 overflow-x-auto" // the padding top is needed because of the overflow, it's half the size of the close button
                            attachments={attachments}
                            onRemoveAttachment={onRemoveAttachment}
                          />
                        )}
                      </div>
                    )}
                  </>
                );
              }}
            </ClientOnly>

            <ClientOnly fallback={<div className="border-b-px border-transparent" />}>
              {() => (
                <>
                  <PromptInspectedElement hasAttachments={hasAttachments} />
                  <PromptFocusedFiles hasAttachments={hasAttachments} />
                </>
              )}
            </ClientOnly>

            <div className="relative select-none">
              <textarea
                ref={textareaRef}
                disabled={disableInput || isReadOnly}
                className={classNames(
                  'w-full pl-4 pt-4 pr-16 focus:outline-none resize-none text-md text-bolt-elements-textPrimary placeholder-bolt-elements-textTertiary bg-transparent text-sm',
                  {
                    'hover:cursor-not-allowed': disableInput || isReadOnly,
                  },
                )}
                onMouseDown={(event) => event.stopPropagation()}
                onPaste={handlePaste}
                onKeyDown={(event) => {
                  if (
                    event.key !== 'Enter' ||
                    event.shiftKey ||
                    event.nativeEvent.isComposing ||
                    event.keyCode === 229
                  ) {
                    return;
                  }

                  if (event.currentTarget.value.startsWith('/')) {
                    return;
                  }

                  event.preventDefault();

                  handleSendMessage();
                }}
                value={input}
                onChange={(event) => {
                  handleInputChange?.(event);
                }}
                style={{
                  minHeight: TEXTAREA_MIN_HEIGHT,
                  maxHeight: TEXTAREA_MAX_HEIGHT,
                }}
                placeholder={placeholder}
                translate="no"
              />

              <ClientOnly>
                {() => (
                  <SendButton
                    show={!disableInput && (input.length > 0 || isStreaming)}
                    isStreaming={isStreaming}
                    onClick={() => {
                      if (isStreaming) {
                        handleStop?.();
                        return;
                      }

                      handleSendMessage();
                    }}
                  />
                )}
              </ClientOnly>
            </div>

            <div className="flex justify-between text-sm p-4 pt-2">
              <div className="flex gap-1 items-center w-full">
                <input
                  type="file"
                  aria-hidden="true"
                  tabIndex={-1}
                  ref={fileInputRef}
                  style={{ display: 'none', visibility: 'hidden' }}
                  accept={SUPPORTED_PROMPT_FILE_TYPES_LIST}
                  multiple
                  onChange={handleFilesSelected}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                />
                <LegacyIconButton
                  title="Upload files"
                  aria-label="Upload files"
                  disabled={isStreaming || isReadOnly}
                  side="top"
                  sideOffset={4}
                  onClick={handleSelectFile}
                >
                  <div className="i-ph:link-simple text-xl" />
                </LegacyIconButton>
                <LegacyIconButton
                  title="Enhance prompt"
                  disabled={input.length === 0 || enhancingPrompt || input.startsWith('/') || isReadOnly}
                  className={classNames({
                    'opacity-100!': enhancingPrompt,
                    'text-bolt-elements-item-contentAccent! pr-1.5 enabled:hover:bg-bolt-elements-item-backgroundAccent!':
                      promptEnhanced,
                  })}
                  side="top"
                  sideOffset={4}
                  onClick={() => enhancePrompt?.()}
                >
                  {enhancingPrompt ? (
                    <>
                      <div className="i-svg-spinners:90-ring-with-bg text-bolt-elements-loader-progress text-xl" />
                      <div className="ml-1.5 text-xs">Enhancing prompt...</div>
                    </>
                  ) : (
                    <>
                      <div className="i-bolt:stars text-xl" />
                      {promptEnhanced && <div className="ml-1.5 text-xs">Prompt enhanced</div>}
                    </>
                  )}
                </LegacyIconButton>
                {chatStarted && (
                  <LegacyIconButton
                    title="Toggle between discussion and build mode"
                    aria-label="Toggle mode"
                    className={classNames({
                      'text-bolt-elements-item-contentAccent! pr-1.5 bg-bolt-elements-item-backgroundAccent!':
                        mode === 'discussion',
                    })}
                    side="top"
                    sideOffset={4}
                    disabled={isStreaming || !onModeChange || isReadOnly}
                    onClick={() => onModeChange?.(mode === 'discussion' ? 'build' : 'discussion')}
                  >
                    <div className="i-ph:chats text-xl" />
                    {mode === 'discussion' && <span className="ml-1.5 text-xs">Discuss</span>}
                  </LegacyIconButton>
                )}

                {chatStarted && (
                  <LegacyIconButton
                    title="Open Prompt Library"
                    aria-label="Open Prompt Library"
                    disabled={!!input.length || isReadOnly}
                    side="top"
                    sideOffset={4}
                    onClick={() => setInput('/')}
                  >
                    <span className="i-bolt:slash-command text-bolt-ds-iconSecondary size-5" />
                  </LegacyIconButton>
                )}

                {userData && (
                  <LegacyIconButton
                    title="Search Help Center"
                    aria-label="Search Help Center"
                    disabled={!!input.length}
                    side="top"
                    sideOffset={4}
                    onClick={debouncedShowHelpbar}
                  >
                    <span className="i-ph:question text-bolt-ds-iconSecondary size-5" />
                  </LegacyIconButton>
                )}

                <div className="flex-1"></div>
              </div>

              <div className="flex items-center gap-3">
                <ClientOnly>{() => <FeaturePreviews />}</ClientOnly>
              </div>
            </div>
          </div>
        </div>
        <div className="pb-[var(--chat-padding)]" />
      </div>
    );
  }),
);
